import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Star, ThumbsUp, CheckCircle, Calendar, ArrowLeft, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { reviewService } from '../../../services/reviewService';
import Header from '../../../components/ui/Header';
import AppImage from '../../../components/AppImage';
import ImageGallery from './components/ImageGallery';
import RelatedReviews from './components/RelatedReviews';

const ReviewDetails = () => {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [markingHelpful, setMarkingHelpful] = useState(false);
  const [hasMarkedHelpful, setHasMarkedHelpful] = useState(false);

  useEffect(() => {
    fetchReview();
  }, [reviewId]);

  const fetchReview = async () => {
    try {
      setLoading(true);
      const data = await reviewService?.getReviewById(reviewId);
      
      if (!data) {
        navigate('/reviews');
        return;
      }
      
      setReview(data);
    } catch (error) {
      console.error('Failed to fetch review:', error);
      navigate('/reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkHelpful = async () => {
    if (hasMarkedHelpful || markingHelpful) return;

    try {
      setMarkingHelpful(true);
      const updatedReview = await reviewService?.markReviewHelpful(reviewId);
      setReview(updatedReview);
      setHasMarkedHelpful(true);
      
      // Store in localStorage to prevent multiple votes
      localStorage.setItem(`helpful_${reviewId}`, 'true');
    } catch (error) {
      console.error('Failed to mark review helpful:', error);
    } finally {
      setMarkingHelpful(false);
    }
  };

  useEffect(() => {
    // Check if user has already marked this review helpful
    const hasVoted = localStorage.getItem(`helpful_${reviewId}`);
    if (hasVoted) {
      setHasMarkedHelpful(true);
    }
  }, [reviewId]);

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Star
            key={star}
            className={`w-6 h-6 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400' :'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (!review) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Link
          to="/reviews"
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to all reviews</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Review Header */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              {/* Product Info */}
              <Link
                to={`/product-review/${review?.products?.slug}`}
                className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <AppImage
                  src={review?.products?.imageUrl}
                  alt={review?.products?.imageAlt || `${review?.products?.name} product image`}
                  className="w-20 h-20 object-cover rounded"
                />
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">
                    {review?.products?.name}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {review?.products?.brand}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-lg font-bold text-blue-600">
                      ${review?.products?.currentPrice}
                    </span>
                    {review?.products?.discount > 0 && (
                      <>
                        <span className="text-sm text-gray-400 line-through">
                          ${review?.products?.originalPrice}
                        </span>
                        <span className="text-sm font-semibold text-green-600">
                          {review?.products?.discount}% OFF
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <ExternalLink className="w-5 h-5 text-gray-400" />
              </Link>

              {/* Reviewer Info */}
              <div className="flex items-start gap-4 mb-6">
                <AppImage
                  src={review?.avatarUrl}
                  alt={review?.avatarAlt || `${review?.reviewerName} avatar`}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-gray-900">
                      {review?.reviewerName}
                    </h3>
                    {review?.isVerified && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded-full">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-medium text-green-700">
                          Verified Purchase
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {format(new Date(review?.createdAt), 'MMMM dd, yyyy')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div className="mb-6">
                {renderStars(review?.rating)}
              </div>

              {/* Badges */}
              <div className="flex items-center gap-2 flex-wrap">
                {review?.isAiGenerated && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-700">
                    AI Generated Review
                  </span>
                )}
                {review?.isVerified && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                    Verified Purchase
                  </span>
                )}
              </div>
            </div>

            {/* Review Content */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                {review?.title}
              </h1>
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {review?.content}
                </p>
              </div>
            </div>

            {/* Review Images */}
            {review?.reviewImages?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Review Images
                </h3>
                <ImageGallery images={review?.reviewImages} />
              </div>
            )}

            {/* Helpful Button */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <p className="text-sm text-gray-600 mb-3">
                Was this review helpful?
              </p>
              <button
                onClick={handleMarkHelpful}
                disabled={hasMarkedHelpful || markingHelpful}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  hasMarkedHelpful
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed' :'bg-blue-50 text-blue-600 hover:bg-blue-100'
                }`}
              >
                <ThumbsUp className={`w-5 h-5 ${hasMarkedHelpful ? 'fill-current' : ''}`} />
                <span className="font-medium">
                  {hasMarkedHelpful ? 'Marked as Helpful' : 'Helpful'}
                </span>
                <span className="text-sm">
                  ({review?.helpfulCount})
                </span>
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Reviewer Stats */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6 sticky top-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                About this Review
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Rating</span>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-gray-900">
                      {review?.rating}.0
                    </span>
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  </div>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Helpful votes</span>
                  <span className="text-lg font-bold text-gray-900">
                    {review?.helpfulCount}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Posted</span>
                  <span className="text-sm font-medium text-gray-900">
                    {format(new Date(review?.createdAt), 'MMM dd, yyyy')}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-sm text-gray-600">Verification</span>
                  <span className={`text-sm font-medium ${
                    review?.isVerified ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {review?.isVerified ? 'Verified' : 'Not Verified'}
                  </span>
                </div>
              </div>

              {/* View Product Button */}
              <Link
                to={`/product-review/${review?.products?.slug}`}
                className="block w-full mt-6 px-4 py-3 bg-blue-600 text-white text-center font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                View Product Details
              </Link>
            </div>

            {/* Related Reviews */}
            {review?.products?.id && (
              <RelatedReviews
                productId={review?.products?.id}
                currentReviewId={reviewId}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewDetails;